import { defineComponent, h, PropType } from 'vue';
import { Dialog, utility } from '@phi/components';
import { Length } from '../../Units';
import { processReadValueModal } from '../../ui_display';
import { FormNumber } from '@components/form';
import { read_value_props as props, read_value_props } from '../../state/modals';
import { from_normalized_to_actual_screen_coordinates } from '../../utility';
import { from_3d_coordinates_to_2d_screen_coordinates } from '../../geometry_and_math';
import { px } from '@phi/util/css';
import { pipe } from '@vaaas/fpts/function';

export default defineComponent({
    props: {
        show: { required: true, type: Boolean},
        display_units: {
            required: false,
            type: String as PropType<Length>,
            default: Length.cm,
        },
    },

    data() {
        return {
            left: undefined as undefined | number,
            right: undefined as undefined | number,
            top: undefined as undefined | number,
            bottom: undefined as undefined | number,
        };
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    watch: {
        async show(value: typeof this.show) {
            if (value) this.reposition();
        },
    },

    computed: {
        title() {
            switch (props.type) {
                case undefined:
                    return '...';

                case 'ringx':
                    return 'Rotate around red axis';

                case 'ringy':
                    return 'Rotate around green axis';

                case 'ringz':
                    return 'Rotate around blue axis';

                case 'rodx':
                    return 'Translate along red axis';

                case 'rody':
                    return 'Translate along green axis';

                case 'rodz':
                    return 'Translate along blue axis';

                case 'tube':
                    return 'Translate along edge';
            }
        },

        unit_type() {
            switch (props.type) {
                case undefined:
                    return '...';

                case 'ringx':
                case 'ringy':
                case 'ringz':
                    return 'Angle';

                case 'rodx':
                case 'rody':
                case 'rodz':
                case 'tube':
                    return 'Distance';
            }
        },

        unit() {
            switch (props.type) {
                case undefined:
                    return '...';

                case 'ringx':
                case 'ringy':
                case 'ringz':
                    return 'degrees';

                default:
                    return this.display_units;
            }
        },

        label() {
            return `${this.unit_type} (in ${this.unit})`;
        },
    },

    methods: {
        async on_confirm() {
            this.$emit('update:show', false);
            if (read_value_props.type)
                processReadValueModal(read_value_props.value, read_value_props.type);
        },

        async reposition() {
            if (!uid_rigid_move_manipulator) return;
            const v = pipe(
                uid_rigid_move_manipulator.center,
                from_3d_coordinates_to_2d_screen_coordinates,
                from_normalized_to_actual_screen_coordinates,
            );
            if (v.x < window.innerWidth / 2) {
                this.left = v.x;
                this.right = undefined;
            } else {
                this.left = undefined;
                this.right = window.innerWidth - 100 - v.x;
            }
            if (v.y < window.innerHeight / 2) {
                this.top = v.y;
                this.bottom = undefined;
            } else {
                this.top = undefined;
                this.bottom = window.innerHeight - 100 - v.y;
            }
        },
    },

    render() {
        return h(Dialog, {
            backdrop: false,
            show: this.show,
            'onUpdate:show': x => this.$emit('update:show', x),
            close_label: 'Cancel',
            onClose: () => this.$emit('update:show', false),
            confirm_label: 'Set',
            onConfirm: this.on_confirm,
            id: 'readValueModal',
            title: this.title,
            width: 'extra-narrow',
            style: {
                position: 'absolute',
                left: this.left ? px(this.left) : 'auto',
                right: this.right ? px(this.right): 'auto',
                top: this.top ? px(this.top): 'auto',
                bottom: this.bottom ? px(this.bottom): 'auto',
            },
        }, () => [
            h('div', { class: [utility.flex, utility.align_center, utility.gap_3 ]}, [
                h('div', this.label),

                h(FormNumber, {
                    class: [utility.block, utility.flexible],
                    value: props.value,
                    'onUpdate:value': x => props.value = x,
                    change: true,
                }),
            ])
        ]);
    }
});
